import { dashboardApi } from '../api/modules/dashboard.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { peopleMasterSeed } from '../data/master/people';
import { vendorMasterSeed } from '../data/master/vendors';
import { indentSeed } from '../data/master/indents';
import { getAssets } from '../data/master/equipmentAssetsStore';

function mockCounts() {
  const villagers = peopleMasterSeed.filter((p) => p.active !== false);
  const vendors = vendorMasterSeed.filter((v) => v.active !== false);
  const equipment = getAssets();
  const indents = indentSeed;

  const countStatus = (list, key, value) =>
    list.filter((row) => String(row[key] || '').toLowerCase() === value).length;

  return {
    villagers: {
      total: villagers.length,
      pendingKyc: countStatus(villagers, 'verificationStatus', 'pending'),
      verified: countStatus(villagers, 'verificationStatus', 'verified'),
    },
    vendors: {
      total: vendors.length,
      pending: countStatus(vendors, 'verificationStatus', 'pending'),
      verified: countStatus(vendors, 'verificationStatus', 'verified'),
      blocked: countStatus(vendors, 'verificationStatus', 'blocked'),
    },
    equipment: {
      total: equipment.length,
      available: countStatus(equipment, 'operationalStatus', 'available'),
      deployed: countStatus(equipment, 'operationalStatus', 'deployed'),
      pendingKyc: countStatus(equipment, 'status', 'pending'),
    },
    indents: {
      total: indents.length,
      pending: countStatus(indents, 'status', 'pending'),
      recommended: countStatus(indents, 'status', 'recommended'),
      assigned: countStatus(indents, 'status', 'assigned'),
      released: countStatus(indents, 'status', 'released'),
      cancelled: countStatus(indents, 'status', 'cancelled'),
    },
  };
}

export async function getDashboardCounts() {
  if (!USE_API) return mockCounts();
  try {
    return await dashboardApi.getCounts();
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load dashboard'));
  }
}

export async function getVillageSummary() {
  if (!USE_API) {
    // Seed mode: empty — village summary needs live assignment data.
    return [];
  }
  try {
    const rows = await dashboardApi.villageSummary();
    return (Array.isArray(rows) ? rows : []).map((row) => ({
      villageId: row.villageId ?? null,
      villageCode: row.villageCode || '',
      villageName: row.villageName || '',
      equipmentCount: Number(row.equipmentCount) || 0,
      recommendedCount: Number(row.recommendedCount) || 0,
      deployedCount: Number(row.deployedCount) || 0,
      releasedCount: Number(row.releasedCount) || 0,
      avgRating: row.avgRating != null ? Number(row.avgRating) : null,
    }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load village summary'));
  }
}

export async function getBuSiteSummary({ includeEmpty = false } = {}) {
  if (!USE_API) {
    return [];
  }
  try {
    const rows = await dashboardApi.buSiteSummary(
      includeEmpty ? { includeEmpty: 'true' } : {},
    );
    return (Array.isArray(rows) ? rows : []).map((row) => ({
      buId: row.buId ?? null,
      buCode: row.buCode || '',
      buName: row.buName || '',
      siteId: row.siteId != null ? Number(row.siteId) : null,
      siteCode: row.siteCode || '',
      siteName: row.siteName || '',
      projectOperation: row.projectOperation || '',
      requestTotal: Number(row.requestTotal) || 0,
      requestPending: Number(row.requestPending) || 0,
      requestRecommended: Number(row.requestRecommended) || 0,
      requestAssigned: Number(row.requestAssigned) || 0,
      requestReleased: Number(row.requestReleased) || 0,
      requestCancelled: Number(row.requestCancelled) || 0,
      deploymentCount: Number(row.deploymentCount) || 0,
      releaseCount: Number(row.releaseCount) || 0,
      activeDeployments: Number(row.activeDeployments) || 0,
      avgRating: row.avgRating != null ? Number(row.avgRating) : null,
      avgTurnaroundDays:
        row.avgTurnaroundDays != null ? Number(row.avgTurnaroundDays) : null,
      vendorTotal: Number(row.vendorTotal) || 0,
      vendorPending: Number(row.vendorPending) || 0,
      vendorVerified: Number(row.vendorVerified) || 0,
      equipmentAvailable: Number(row.equipmentAvailable) || 0,
    }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load BU / Site summary'));
  }
}
